/**
 * Disclaimer Page — Legacy Triangle
 */

import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { AlertTriangle, Mail, Phone } from "lucide-react";

export default function Disclaimer() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0f2d52]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0f2d52] via-[#1a3a5c] to-[#0f2d52]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#e8821c]/10 border border-[#e8821c]/30 rounded-full px-4 py-2 mb-6">
            <AlertTriangle className="w-4 h-4 text-[#e8821c]" />
            <span className="text-[#e8821c] text-sm font-medium">Important Notice</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Disclaimer
          </h1>
          <p className="text-white/70 text-lg">Last updated: March 18, 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none" style={{ fontFamily: "'Inter', sans-serif" }}>
            
            {/* Important Notice Box */}
            <div className="bg-[#e8821c]/10 border border-[#e8821c]/30 rounded-xl p-6 mb-10">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-[#e8821c] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-[#0f2d52] mb-2">Please Read Carefully</h3>
                  <p className="text-gray-600">
                    The information provided on this website and through our services is for general informational purposes only. It is not intended as financial, legal, or professional advice. Always consult with qualified professionals before making any financial decisions.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">1. General Information</h2>
              <p className="text-gray-600 leading-relaxed">
                Legacy Triangle provides business funding consultation, credit building guidance, and investment information. The content on this website is provided for informational purposes only and should not be construed as professional financial, legal, tax, or investment advice. We recommend that you consult with appropriate professionals regarding your specific situation before making any financial decisions.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">2. No Guarantee of Results</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                While we strive to provide valuable guidance and connections to funding sources, we cannot and do not guarantee:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Approval of any funding application</li>
                <li>Specific funding amounts or terms</li>
                <li>Improvement in credit scores or business credit profiles</li>
                <li>Any particular outcome or result from using our services</li>
                <li>Returns on any investment opportunities</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                Past performance and testimonials are not indicative of future results. Individual results may vary significantly based on numerous factors including creditworthiness, business performance, market conditions, and lender criteria.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">3. Broker/Consultant Role</h2>
              <p className="text-gray-600 leading-relaxed">
                Legacy Triangle operates as a business funding consultant and broker. We are not a lender, bank, or direct provider of funds. We connect businesses with potential funding sources and provide guidance throughout the application process. All funding decisions are made solely by our lending partners based on their own underwriting criteria and policies.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">4. Investment Disclaimer</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Information about investment opportunities, including private real estate lending and business acquisitions, is provided for informational purposes only. Such investments involve substantial risk and are suitable only for accredited investors who can afford to lose their entire investment.
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>All investments carry risk, including the potential loss of principal</li>
                <li>Past performance does not guarantee future results</li>
                <li>Projected returns are estimates only and are not guaranteed</li>
                <li>Real estate investments are illiquid and may be difficult to sell</li>
                <li>Investors should conduct their own due diligence before investing</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">5. Credit Information</h2>
              <p className="text-gray-600 leading-relaxed">
                Information provided about credit building, credit scores, and credit reporting is educational in nature. Credit scoring models and requirements vary among credit bureaus and lenders. We do not guarantee any specific credit score improvements or outcomes. Credit building takes time and results depend on individual circumstances and consistent positive credit behavior.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">6. Third-Party Content</h2>
              <p className="text-gray-600 leading-relaxed">
                Our website may contain links to third-party websites, resources, or content. These links are provided for convenience only. We do not endorse, control, or assume responsibility for the content, products, services, or practices of any third-party sites. Your use of third-party websites is at your own risk and subject to the terms and conditions of those sites.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">7. Testimonials and Success Stories</h2>
              <p className="text-gray-600 leading-relaxed">
                Testimonials and success stories displayed on our website represent the experiences of individual clients. These results are not typical and should not be construed as a guarantee of similar results. Each client's situation is unique, and outcomes depend on many factors beyond our control.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">8. Accuracy of Information</h2>
              <p className="text-gray-600 leading-relaxed">
                While we make every effort to ensure the accuracy of information on our website, we do not warrant that the content is complete, accurate, or current. Information may change without notice. We are not responsible for any errors or omissions, or for the results obtained from the use of such information.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">9. Professional Advice</h2>
              <p className="text-gray-600 leading-relaxed">
                The information on this website does not constitute professional advice. Before making any financial, legal, or business decisions, you should consult with qualified professionals including financial advisors, attorneys, accountants, and other relevant experts who can evaluate your specific situation and needs.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed">
                To the maximum extent permitted by law, Legacy Triangle and its officers, directors, employees, and agents shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our website or services, including but not limited to any decisions made or actions taken in reliance on the information provided.
              </p>
            </div>

            <div className="bg-[#0f2d52]/5 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">Questions?</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you have any questions about this Disclaimer, please contact us:
              </p>
              <div className="space-y-3">
                <a href="mailto:info@legacytriangle.com" className="flex items-center gap-3 text-[#0f2d52] hover:text-[#48d480] transition-colors">
                  <Mail className="w-5 h-5 text-[#48d480]" />
                  info@legacytriangle.com
                </a>
                <a href="tel:+19844644672" className="flex items-center gap-3 text-[#0f2d52] hover:text-[#48d480] transition-colors">
                  <Phone className="w-5 h-5 text-[#48d480]" />
                  (984) 464-4672
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
